/*!
 * Skyn Iceland — accessibility helpers (pure JS) — vNext (consolidated, non-destructive)
 * Goals:
 * - Single scheduler + single observer (no duplicated observers / listeners)
 * - Idempotent patches (data-a11y-* markers)
 * - Avoid overwriting author-provided semantics (only fill gaps)
 * - Directly addresses recurring audit items:
 *   • role="button" missing accessible name
 *   • Flickity tabindex keyboard trap on carousel roots
 *   • Empty/unnamed links in carousels/overlays
 *   • Redundant/decorative grid image alts (incl. Mosaic)
 *   • Hidden-content / "semantic-hidden" hardening:
 *       - If aria-hidden/hidden => prevent tab focus, inert when possible
 *       - IMPORTANT: remove inert we added when element becomes visible again (Flickity slides)
 *
 * Safe to keep long-term.
 */

// Global selector list for product/collection grids used by a11y observers
window.GRID_CONTAINERS = window.GRID_CONTAINERS || [
  '.product-grid', '.grid', '.grid--products', '.collection', '.collection-grid',
  '.product-list', '.products-listing', '.related-products', '.recommendations',
  '[data-related-products]', '[data-product-list]', '.featured-collection',
  '.home-collection', '.index-section', '.shopify-section--featured-collection',
  '.product-recommendations', '.shopify-section--collection-list', '.collection-list',
  '.best-sellers', '[data-section-type*="collection"]', '[data-section-type*="featured"]',
  // Mosaic / image-tile sections
  '.mosaic', '.mosaic-grid', '.image-mosaic', '.mosaic__grid',
  '.mosaic__container', '.mosaic__wrapper',
  '[data-section-type*="mosaic"]', '[class*="mosaic"]'
].join(',');

(function () {
  'use strict';

  // Prevent double-boot if this file is included twice
  if (window.__SKYN_A11Y_BOOTED__) return;
  window.__SKYN_A11Y_BOOTED__ = true;

  // ---------- tiny utils ----------
  const $$ = (sel, root = document) => Array.from((root || document).querySelectorAll(sel));
  const $  = (sel, root = document) => (root || document).querySelector(sel);

  const ready = (fn) => (document.readyState === 'loading')
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

  const norm = (s) => (s || '').toString().replace(/\s+/g, ' ').trim();

  const isVisible = (el) => {
    if (!el || !(el instanceof Element)) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    // offsetParent null for fixed elements, so allow fixed
    return el.offsetParent !== null || cs.position === 'fixed';
  };

  const isHidden = (el) => {
    if (!el || !(el instanceof Element)) return true;
    if (el.hasAttribute('hidden')) return true;
    if (el.getAttribute('aria-hidden') === 'true') return true;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return true;
    return false;
  };

  // Mark-once helper (returns true when it marks, false if already marked)
  const markOnce = (el, key) => {
    if (!el || !(el instanceof Element)) return false;
    if (el.hasAttribute(key)) return false;
    el.setAttribute(key, 'true');
    return true;
  };

  // Accessible-name check (lightweight, conservative)
  function hasA11yName(el){
    if (!el || !(el instanceof Element)) return false;
    if (norm(el.getAttribute('aria-label'))) return true;

    const labelledby = norm(el.getAttribute('aria-labelledby'));
    if (labelledby) {
      const ids = labelledby.split(/\s+/).filter(Boolean);
      const txt = norm(ids.map(id => document.getElementById(id)?.textContent || '').join(' '));
      if (txt) return true;
    }

    // innerText respects visibility; if there is visible text, it's named
    if (norm(el.innerText)) return true;

    // Named SVG
    if (el.querySelector('svg[aria-label], svg title')) return true;

    // Non-empty alt within
    if (el.querySelector('img[alt]:not([alt=""])')) return true;

    return false;
  }

  // ---------- live region announcer ----------
  function ensureLiveRegion(){
    let live = document.getElementById('a11y-status');
    if (!live) {
      live = document.createElement('div');
      live.id = 'a11y-status';
      live.className = 'sr-only';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      document.body.appendChild(live);
    } else {
      if (!live.getAttribute('aria-live')) live.setAttribute('aria-live','polite');
      if (!live.getAttribute('aria-atomic')) live.setAttribute('aria-atomic','true');
    }
    return live;
  }

  const live = ensureLiveRegion();
  window.__a11yAnnounce = function (msg) {
    if (!live) return;
    live.textContent = '';
    setTimeout(() => { live.textContent = String(msg); }, 60);
  };

  // ---------- minimal CSS helpers (idempotent) ----------
  (function injectStyle(){
    if (document.getElementById('a11y-style')) return;
    const style = document.createElement('style');
    style.id = 'a11y-style';
    style.textContent = `
      .a11y-target-min,[data-a11y-target]{min-width:24px;min-height:24px;display:inline-flex;align-items:center;justify-content:center}
      .sr-only,.visually-hidden{position:absolute!important;width:1px;height:1px;overflow:hidden;clip:rect(1px,1px,1px,1px);white-space:nowrap}
      :focus-visible{outline:2px solid currentColor;outline-offset:2px}
    `;
    document.head.appendChild(style);
  })();

  // ---------- feature flags ----------
  const FLAGS = {
    // Image de-dupe rules are intentionally conservative
    BLANK_GRID_ALTS: true,
    BLANK_RELATED_ALTS: true,
    DECORATE_ONLY_IN_LINKS: true,
    REMOVE_LABELS_COPIED_FROM_IMG_ALT: true,

    // Hidden-content / semantic-hidden hardening
    SEMANTIC_HIDDEN_HARDENING: true,

    // Optional: de-dupe press logo alts inside logo sliders (off unless you enable)
    PRESS_LOGO_ALT_DEDUP: false
  };

  const GRID_CONTAINERS = window.GRID_CONTAINERS;

  // ---------- 1) External links (target=_blank) ----------
  function enhanceExternalLinks(root = document){
    const anchors = root?.matches?.('a[target="_blank"]') ? [root] : $$('a[target="_blank"]', root);
    anchors.forEach(a => {
      const rel = (a.getAttribute('rel') || '').toLowerCase();
      if (!rel.includes('noopener')) a.setAttribute('rel', (rel + ' noopener').trim());
      if (!rel.includes('noreferrer')) a.setAttribute('rel', (a.getAttribute('rel') + ' noreferrer').trim());

      if (!a.querySelector('.sr-only[data-a11y-newtab]')) {
        const s = document.createElement('span');
        s.className = 'sr-only';
        s.setAttribute('data-a11y-newtab','true');
        s.textContent = ' (opens in a new tab)';
        a.append(s);
      }
    });
  }

  // ---------- 2) Icon-only controls + minimum target sizing ----------
  const CONTROL_SELECTORS = [
    ['[data-search-toggle], summary.search-popdown__toggle, [data-popdown-toggle][aria-haspopup="dialog"]', 'Open search'],
    ['[data-drawer-close], .drawer__close, [data-modal-close], .modal__close', 'Close'],
    ['.slick-prev, .swiper-button-prev, .splide__arrow--prev', 'Previous slide'],
    ['.slick-next, .swiper-button-next, .splide__arrow--next', 'Next slide'],
    ['.launcher-button', 'Open rewards']
  ];

  function labelIconControls(root = document){
    CONTROL_SELECTORS.forEach(([sel, label]) => {
      const els = root?.matches?.(sel) ? [root] : $$(sel, root);
      els.forEach(el => {
        if (!hasA11yName(el)) el.setAttribute('aria-label', label);
        el.classList.add('a11y-target-min');
        el.setAttribute('data-a11y-target', '');
      });
    });

    const overlayButtons = root?.matches?.('[class*="ada-cc"],[class*="aa-cc"]') ? [root] : $$('[class*="ada-cc"],[class*="aa-cc"]', root);
    overlayButtons
      .filter(el => el.getAttribute('role') === 'button')
      .forEach(btn => {
        if (!hasA11yName(btn)) btn.setAttribute('aria-label', 'Accessibility options');
      });
  }

  // ---------- 3) Scoped missing labels ----------
  function addScopedLabels(root = document){
    const scopes = ['header', '.site-header', '.footer', '.site-footer', '.search', '[role="search"]', '.newsletter', 'form[action*="contact"]'];
    scopes.forEach(scope => {
      const containers = root?.matches?.(scope) ? [root] : $$(scope, root);
      containers.forEach(container => {
        $$('input[type="email"],input[type="search"]', container).forEach(input => {
          if (input.hasAttribute('data-a11y-labeled')) return;

          if (!input.id) input.id = 'in_' + Math.random().toString(36).slice(2, 9);
          const id = input.id;

          const already =
            document.querySelector(`label[for="${CSS.escape(id)}"]`) ||
            norm(input.getAttribute('aria-label')) ||
            norm(input.getAttribute('aria-labelledby'));

          if (!already) {
            const lab = document.createElement('label');
            lab.className = 'sr-only';
            lab.setAttribute('for', id);
            lab.textContent = input.type === 'search' ? 'Search' : 'Email address';
            input.parentNode && input.parentNode.insertBefore(lab, input);
          }

          input.setAttribute('data-a11y-labeled','true');
        });
      });
    });
  }

  // ---------- 4) Dialog semantics + focus trap + Esc close ----------
  const focusableSel = 'a,button,input,select,textarea,[tabindex]:not([tabindex="-1"])';

  function trapFocus(container){
    if (!markOnce(container, 'data-a11y-trap')) return;

    function onKey(e){
      if (e.key === 'Escape'){
        const closer = $('[data-drawer-close], [data-modal-close], .modal__close, .drawer__close, .pswp__button--close', container) || container;
        closer?.click?.();
        container.dispatchEvent(new CustomEvent('a11y:close'));
      }
      if (e.key !== 'Tab') return;

      const items = $$(focusableSel, container).filter(el => !el.hasAttribute('disabled') && isVisible(el));
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first){ e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last){ e.preventDefault(); first.focus(); }
    }

    container.addEventListener('keydown', onKey);
    container.addEventListener('a11y:close', () => container.removeEventListener('keydown', onKey), { once: true });
  }

  function armDialog(el){
    if (!el || !(el instanceof Element)) return;
    const looksLikeDialog = el.matches('[role="dialog"], dialog, .modal, .drawer, [data-modal], [data-drawer], [aria-modal="true"]');
    if (!looksLikeDialog) return;

    if (el.getAttribute('role') !== 'dialog') el.setAttribute('role', 'dialog');
    if (!el.getAttribute('aria-modal')) el.setAttribute('aria-modal', 'true');

    if (!norm(el.getAttribute('aria-label')) && !norm(el.getAttribute('aria-labelledby'))){
      const h = $('h1,h2,h3', el);
      if (h && norm(h.textContent)) el.setAttribute('aria-label', norm(h.textContent));
    }

    trapFocus(el);

    const mo = new MutationObserver(() => {
      if (!isVisible(el)) return;
      const isOpen = el.hasAttribute('open') || el.getAttribute('aria-hidden') === 'false' || !el.hasAttribute('aria-hidden');
      if (isOpen){
        const first = $(focusableSel, el);
        first?.focus?.();
        window.__a11yAnnounce?.('Dialog opened');
      }
    });

    mo.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'open', 'hidden', 'aria-hidden'] });
    el.addEventListener('a11y:close', () => window.__a11yAnnounce?.('Dialog closed'));
  }

  function armExistingDialogs(root = document){
    const dialogSel = 'dialog,[data-modal],[data-drawer],[role="dialog"],.modal,.drawer,[aria-modal="true"],.pswp';
    const dialogs = root?.matches?.(dialogSel) ? [root] : $$(dialogSel, root);
    dialogs.forEach(armDialog);
  }

  // ---------- 5) Pause autoplaying sliders ----------
  function pauseAutoplay(){
    if (window.jQuery && window.jQuery.fn && window.jQuery.fn.slick) {
      $$('.slick-initialized').forEach(el => { try { window.jQuery(el).slick('slickPause'); } catch(e){} });
    }
    $$('.swiper').forEach(el => {
      const inst = el.swiper || el._swiper;
      try { inst?.autoplay?.stop?.(); } catch(e){}
    });
    $$('.splide').forEach(el => {
      const inst = el.splide || el._splide;
      try { inst?.Components?.Autoplay?.pause?.(); } catch(e){}
    });
  }

  // ---------- 6) Flickity tabindex removal ----------
  function fixFlickityTabindex(root = document){
    const roots = root?.matches?.('.flickity-enabled,[data-flickity]') ? [root] : $$('.flickity-enabled,[data-flickity]', root);
    roots.forEach(r => {
      if (r.getAttribute('tabindex') === '0') r.removeAttribute('tabindex');
      r.querySelectorAll('.flickity-viewport[tabindex="0"], .flickity-slider[tabindex="0"]').forEach(el => el.removeAttribute('tabindex'));
    });
  }

  // ---------- 7) Announce cart count changes ----------
  function watchCart(){
    const cartBadge = $('[data-cart-count], .cart-count, #CartCount, [aria-label*="cart"] .count');
    if (!cartBadge || !('MutationObserver' in window)) return;
    if (!markOnce(cartBadge, 'data-a11y-cartwatch')) return;

    new MutationObserver(() => {
      const n = (cartBadge.textContent || '').replace(/[^0-9]/g, '');
      if (n) window.__a11yAnnounce?.(`Cart updated. ${n} item${n === '1' ? '' : 's'} in cart.`);
    }).observe(cartBadge, { childList: true, characterData: true, subtree: true });
  }

  // ---------- 8) Unique names for every visible <nav> ----------
  function ensureUniqueNavNames() {
    const SELECTOR = 'nav,[role="navigation"]';
    const re = {
      mobile: /(mobile|drawer|offcanvas|sidebar|menu)/i,
      footer: /(footer|bottom)/i,
      breadcrumb: /crumb/i,
      social: /social/i,
      primary: /(NavStandard|primary|main|site(-|_)?nav|nav--primary|header)/i,
      sticky: /(sticky|affix|fix(ed)?)/i
    };

    function textOf(el) { return norm(el && el.textContent); }
    function headingText(nav) {
      const h = nav.querySelector(':scope h1,h2,h3,h4,h5,h6,.visually-hidden,[class*="sr-only"]');
      return textOf(h);
    }
    function getAccessibleName(nav) {
      const labelledby = norm(nav.getAttribute('aria-labelledby'));
      if (labelledby) {
        const ids = labelledby.split(/\s+/).filter(Boolean);
        const txt = norm(ids.map(id => document.getElementById(id)).filter(Boolean).map(textOf).join(' '));
        if (txt) return txt;
      }
      if (norm(nav.getAttribute('aria-label'))) return norm(nav.getAttribute('aria-label'));
      if (norm(nav.getAttribute('title'))) return norm(nav.getAttribute('title'));
      return '';
    }
    function contextLabel(nav) {
      const id = nav.id || '';
      const cls = nav.className || '';
      if (nav.closest('footer,[role="contentinfo"]')) return 'Footer navigation';
      if (re.breadcrumb.test(id) || re.breadcrumb.test(cls)) return 'Breadcrumbs';
      if (re.social.test(id) || re.social.test(cls)) return 'Social links';
      if (re.mobile.test(id) || re.mobile.test(cls) || nav.closest('.drawer,.offcanvas,[data-drawer],.mobile-menu')) return 'Mobile navigation';
      const pos = getComputedStyle(nav).position;
      if (re.sticky.test(id) || re.sticky.test(cls) || pos === 'fixed') return 'Primary navigation (sticky)';
      if (re.primary.test(id) || re.primary.test(cls)) return 'Primary navigation';
      const h = headingText(nav);
      if (h) return h;
      return 'Navigation';
    }

    const used = new Map();
    const navs = Array.from(document.querySelectorAll(SELECTOR));

    navs.forEach(nav => {
      if (!isVisible(nav)) return;

      let name = getAccessibleName(nav);
      if (!name) {
        name = contextLabel(nav);
        nav.setAttribute('aria-label', name);
      }

      const count = (used.get(name) || 0) + 1;
      used.set(name, count);

      if (count > 1) {
        const cs = getComputedStyle(nav);
        let specialized = name;

        if (!/sticky/i.test(name) && cs.position === 'fixed') specialized = name + ' (sticky)';
        else if (!/mobile/i.test(name) && (re.mobile.test(nav.className) || nav.closest('.drawer,.offcanvas,[data-drawer],.mobile-menu'))) specialized = name + ' (mobile)';
        else if (nav.closest('footer,[role="contentinfo"]')) specialized = 'Footer navigation';
        else specialized = name + ' (' + count + ')';

        nav.setAttribute('aria-label', specialized);
        used.set(specialized, 1);
      }
    });
  }
  window.__ensureUniqueNavNames = ensureUniqueNavNames;

  // ---------- 9) Announcement bar landmark normalization ----------
  function fixAnnouncementLandmarks(root=document){
    const INNER_SEL = '[data-announcement-wrapper]';
    const OUTER_SEL = '.shopify-section.announcement-bar-static';

    function isInsideLandmark(el){
      return !!el.closest('header, nav, main, footer, [role="banner"], [role="navigation"], [role="main"], [role="contentinfo"], [role="region"], [role="complementary"], [role="search"]');
    }
    function ensureInnerLandmark(inner){
      if (!isInsideLandmark(inner)) {
        if (!inner.hasAttribute('role')) inner.setAttribute('role','region');
        if (!norm(inner.getAttribute('aria-label')) && !norm(inner.getAttribute('aria-labelledby'))) {
          inner.setAttribute('aria-label','Site announcements');
        }
      }
    }
    function stripOuterIfDup(inner){
      const outer = inner.closest(OUTER_SEL);
      if (!outer || outer === inner) return;

      const hasRole = outer.hasAttribute('role') && /region|banner|complementary|main|navigation|contentinfo|search/i.test(outer.getAttribute('role'));
      if (!hasRole) return;

      const oName = norm(outer.getAttribute('aria-label')) || '';
      const iName = norm(inner.getAttribute('aria-label')) || '';
      if (!oName || !iName || oName === iName) {
        outer.removeAttribute('role');
        outer.removeAttribute('aria-label');
        outer.removeAttribute('aria-labelledby');
      }
    }

    const inners = root?.matches?.(INNER_SEL) ? [root] : Array.from(root.querySelectorAll(INNER_SEL));
    inners.forEach(inner => { ensureInnerLandmark(inner); stripOuterIfDup(inner); });
  }
  window.__fixAnnouncementLandmarks = fixAnnouncementLandmarks;

  // ---------- 10) Empty link naming ----------
  function fixEmptyLinks(root = document){
    const LINK_SEL  = 'a[href]';
    const TITLE_SEL = '.slide__title, .hero__title, .banner__heading, .heading, h1, h2, h3, .product__title, .card__heading, [data-slide-title], [data-product-title]';
    const SLIDE_SEL = '.swiper-slide, .slick-slide, .splide__slide, .slide, .hero, .banner, .card, .product-card, .featured-product, .collection-hero, section, article, li';

    function getTextFromIds(el, attr){
      const ids = (el.getAttribute(attr) || '').split(/\s+/).filter(Boolean);
      return norm(ids.map(id => document.getElementById(id)?.textContent || '').join(' '));
    }
    function deriveFromHref(href){
      try {
        const u = new URL(href, location.origin);
        const parts = u.pathname.split('/').filter(Boolean);
        const last = parts[parts.length - 1] || '';
        const label = decodeURIComponent(last.replace(/-/g, ' ')).trim();
        if (!label) return '';
        const titled = label.replace(/\b\w/g, c => c.toUpperCase());
        if (parts.includes('products'))    return `View product: ${titled}`;
        if (parts.includes('collections')) return `View collection: ${titled}`;
        return `Open: ${titled}`;
      } catch { return ''; }
    }
    function candidateName(a){
      const title = norm(a.getAttribute('title'));
      if (title) return title;

      const slide = a.closest(SLIDE_SEL);
      const head  = slide?.querySelector(TITLE_SEL);
      if (head?.textContent) return norm(head.textContent);

      const img = slide?.querySelector('img[alt]:not([alt=""])');
      if (img) return norm(img.getAttribute('alt'));

      const slideLabel = norm(slide?.getAttribute('aria-label'));
      if (slideLabel) return slideLabel;

      const slideLb = slide?.hasAttribute('aria-labelledby') ? getTextFromIds(slide, 'aria-labelledby') : '';
      if (slideLb) return slideLb;

      return deriveFromHref(a.getAttribute('href'));
    }

    const links = root?.matches?.(LINK_SEL) ? [root] : $$(LINK_SEL, root);
    links.forEach(a => {
      const href = a.getAttribute('href') || '';
      if (!href || href === '#' || href.startsWith('#')) return;
      if (!isVisible(a)) return;

      if (!hasA11yName(a) || (a.hasAttribute('aria-label') && !norm(a.getAttribute('aria-label')))) {
        const name = candidateName(a);
        if (name) a.setAttribute('aria-label', name);
      }
    });
  }
  window.__fixEmptyLinks = fixEmptyLinks;

  // ---------- 11) Iframe titles ----------
  function fixIframes(root=document){
    function titleFor(src){
      try {
        const u = new URL(src, location.origin);
        if (u.hostname.includes('shopifycloud') && u.pathname.includes('/preview-bar/')) {
          return 'Shopify theme preview bar';
        }
        return `Embedded content from ${u.hostname}`;
      } catch { return 'Embedded content'; }
    }
    const frames = root?.matches?.('iframe') ? [root] : $$('iframe', root);
    frames.forEach(f => {
      const hasTitle = norm(f.getAttribute('title')).length > 0;
      if (!hasTitle) f.setAttribute('title', titleFor(f.getAttribute('src') || ''));
    });
  }
  window.__fixIframes = fixIframes;

  // ---------- 12) Upsell inert enforcement ----------
  function upsellInert(root=document){
    const SEL = '[data-upsell-holder]';
    const els = root?.matches?.(SEL) ? [root] : $$(SEL, root);
    els.forEach(el => {
      const hidden = el.getAttribute('aria-hidden') === 'true';
      if (hidden) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    });
  }
  window.__upsellInert = upsellInert;

  // ---------- 13) Normalize banner landmarks ----------
  function normalizeBanners(root=document){
    const CANDIDATE_SEL = 'header.site-header, header[role="banner"]';

    function isTopLevel(el){
      return !el.closest('main,[role="main"],nav,[role="navigation"],footer,[role="contentinfo"],[role="region"],[role="complementary"],[role="search"],[role="banner"]:not(:scope)');
    }

    function choosePrimary(list){
      return (
        list.find(h => isTopLevel(h) && !h.classList.contains('site-header--compress')) ||
        list.find(h => !h.classList.contains('site-header--compress')) ||
        list[0]
      );
    }

    const all = Array.from(root.querySelectorAll(CANDIDATE_SEL)).filter(isVisible);
    if (!all.length) return;

    const primary = choosePrimary(all);
    if (primary.getAttribute('role') !== 'banner') primary.setAttribute('role','banner');

    all.forEach(h => {
      if (h === primary) return;
      h.setAttribute('role','none');
      h.removeAttribute('aria-label');
      h.removeAttribute('aria-labelledby');
    });
  }
  window.__normalizeBanners = normalizeBanners;

  // ---------- 14) Ensure .whatmore-base is inside a landmark ----------
  function containWhatmore(root=document){
    const SEL = '.whatmore-base';

    function isInsideLandmark(el){
      return !!el.closest('header, nav, main, footer, [role="banner"], [role="navigation"], [role="main"], [role="contentinfo"], [role="region"], [role="complementary"], [role="search"]');
    }

    function deriveLabel(el){ return el.getAttribute('data-region-label') || 'Featured content'; }

    const els = root?.matches?.(SEL) ? [root] : $$(SEL, root);
    els.forEach(el => {
      if (isInsideLandmark(el)) return;
      if (!el.hasAttribute('role')) el.setAttribute('role', 'region');
      if (!norm(el.getAttribute('aria-label')) && !norm(el.getAttribute('aria-labelledby'))) {
        el.setAttribute('aria-label', deriveLabel(el));
      }
      el.setAttribute('data-landmark-patched', 'true');
    });
  }
  window.__containWhatmore = containWhatmore;

  // ---------- 15) Orphan <select> labels ----------
  function labelBundleSelects(root=document){
    const WRAP_SEL = [
      '[class*="rbr-"] select',
      '.rbr-selectBox__container select',
      '.selectBoxWrapper_container_b6a8d330 select',
      '.bundle select', '[data-bundle] select', '[data-upsell] select'
    ].join(',');

    function hasName(el){
      if (norm(el.getAttribute('aria-label'))) return true;
      const ll = norm(el.getAttribute('aria-labelledby'));
      if (ll && ll.split(/\s+/).some(id => document.getElementById(id))) return true;
      if (el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`)) return true;
      return false;
    }

    function ensureId(el){ return el.id || (el.id = 'sel_' + Math.random().toString(36).slice(2,9)); }

    function inferText(select){
      const c = select.closest('.rbr-selectBox__container, .rbr-selectBoxes__container, [class*="rbr-"], .bundle, [data-bundle], [data-upsell]');
      const explicit = c?.querySelector('label, .select-label, [data-label], .rbr-label, .rbr-selectBox__label');
      let txt = norm(explicit?.textContent || explicit?.getAttribute?.('data-label'));
      if (!txt) {
        const first = select.querySelector('option[disabled], option[value=""], option[selected]') || select.querySelector('option');
        const hint = norm(first?.textContent);
        if (hint) txt = hint.replace(/\s*[:*]\s*$/,'');
      }
      return txt || 'Select option';
    }

    const selects = root?.matches?.(WRAP_SEL) ? [root] : $$(WRAP_SEL, root);
    selects.forEach(sel => {
      if (hasName(sel)) return;
      const id = ensureId(sel);
      const txt = inferText(sel);

      let lab = sel.previousElementSibling;
      if (!(lab && lab.tagName === 'LABEL')) {
        lab = document.createElement('label');
        lab.className = 'sr-only';
        sel.parentNode && sel.parentNode.insertBefore(lab, sel);
      }
      lab.setAttribute('for', id);
      if (!norm(lab.textContent)) lab.textContent = txt;
    });
  }
  window.__labelBundleSelects = labelBundleSelects;

  // ---------- 16) Quick Add / ATC button naming ----------
  function nameATCButtons(root=document){
    const BTN_SEL = 'button[name="add"], [data-atc-trigger], [data-add-to-cart], .btn--quick';
    const GENERIC_LABEL = /^(add(?:\s*to\s*cart)?|quick\s*buy)$/i;

    function productTitleFromContexts(el){
      const contexts = [
        el.closest('[data-product-card], .product-card, .product-grid-item, article, li, .card, .mosaic__item, .mosaic-item, .tile'),
        el.closest('[id*="__related-"], .related-products'),
        document.querySelector('main,[role="main"], .product, [data-product]'),
        document
      ];
      for (const ctx of contexts){
        if (!ctx) continue;
        const t = ctx.querySelector('.product__title, .card__heading, .product-title, [data-product-title], h1.product__title, h1[itemprop="name"], .mosaic__title, .tile__title, .mosaic__heading, [data-tile-title]');
        if (t && norm(t.textContent)) return norm(t.textContent);
        const a = ctx.querySelector('a[href*="/products/"][title], a[href*="/products/"] .product-title, a[href*="/products/"]');
        if (a){
          const txt = norm(a.getAttribute('title') || a.textContent);
          if (txt) return txt;
        }
      }
      return norm(document.querySelector('meta[property="og:title"]')?.getAttribute('content'));
    }

    const btns = root?.matches?.(BTN_SEL) ? [root] : $$(BTN_SEL, root);
    btns.forEach(btn => {
      if (!isVisible(btn)) return;

      const labelledby = norm(btn.getAttribute('aria-labelledby'));
      if (labelledby) return;

      const current = norm(btn.getAttribute('aria-label'));
      const title = productTitleFromContexts(btn);
      const label = title ? `Add to cart: ${title}` : 'Add to cart';

      if (!current || GENERIC_LABEL.test(current)) btn.setAttribute('aria-label', label);
    });
  }
  window.__nameATCButtons = nameATCButtons;

  // ---------- 17) Buttonize non-button elements used as buttons ----------
  function buttonizeNonButtons(root=document){
    const CANDIDATE_SEL = [
      '.swiper-button-prev', '.swiper-button-next',
      '[data-trigger]','[data-action]','[data-toggle]','[data-click]',
      '[data-accordion-trigger]','[data-gallery-trigger]',
      '[data-popdown-toggle]','[data-popover-trigger]',
      '[data-modal-open]','[data-dialog-open]',
      '[data-carousel-prev]','[data-carousel-next]',
      '[style*="cursor:pointer"]','[style*="cursor: pointer"]',
      'a:not([href])'
    ].join(',');

    const SKIP_SEL = '.tab, [data-tab], [role="tab"], [role="tablist"] *, [role="tabpanel"] *';

    const isNaturallyInteractive = el => {
      if (/^(BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(el.tagName)) return true;
      if (el.tagName === 'A' && el.hasAttribute('href')) return true;
      return false;
    };

    const hasInteractiveChild = el =>
      !!el.querySelector('a[href],button,input,select,textarea,summary,[role="button"],[role="tab"],[tabindex]:not([tabindex="-1"])');

    function ensureName(el){
      if (hasA11yName(el)) return;
      const guess = norm(
        el.getAttribute('data-title') ||
        el.getAttribute('title') ||
        el.getAttribute('data-label') ||
        el.closest('[data-event-title]')?.getAttribute('data-event-title') ||
        el.closest('div, li, article')?.querySelector('h2, h3, h4, [class*="title"], [class*="name"]')?.textContent
      );
      // Only set a label if we found something meaningful — never fall back to a generic word
      if (guess) el.setAttribute('aria-label', guess);
    }

    const els = root?.matches?.(CANDIDATE_SEL) ? [root] : $$(CANDIDATE_SEL, root);
    els.forEach(el => {
      if (!(el instanceof Element)) return;
      if (el.closest('[aria-hidden="true"]')) return;
      if (el.matches(SKIP_SEL)) return;
      if (!isVisible(el)) return;
      if (isNaturallyInteractive(el)) return;
      if (hasInteractiveChild(el)) return;

      // FIX 1: Skip elements already inside a role="button" to prevent nesting
      if (el.closest('[role="button"]')) return;

      // FIX 2: Skip Whatmore widgets entirely — they handle their own interactions
      if (el.closest('.whatmore-base, .whatmore-shimmer-events-carousel-landscape')) return;

      const role = (el.getAttribute('role')||'').toLowerCase();
      if (role && role !== 'button' && role !== 'presentation' && role !== 'none') return;

      if (role !== 'button') el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex','0');

      ensureName(el);

      if (markOnce(el, 'data-a11y-buttonized')) {
        el.addEventListener('keydown', e => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            el.click();
          }
        });
      }
    });
  }
  window.__buttonizeNonButtons = buttonizeNonButtons;

  // ---------- Post-pass hardeners (image/link name de-dupe, filter semantics) ----------
  function card(node){
    return node.closest(
      '.product-card, .product-grid-item, .grid__item, .card, [data-product-card], ' +
      '.mosaic__item, .mosaic-item, .mosaic__tile, .tile, [data-mosaic-item], ' +
      'li, article'
    );
  }

  function inRelated(node){
    return !!node.closest('[id*="__related-"], .related-products, [data-related-products], #RelatedProducts, [aria-label*="Related"], [aria-labelledby*="related"]');
  }

  function cardHasIndependentName(cardEl){
    if (!cardEl) return false;

    const title = cardEl.querySelector(
      'h1,h2,h3,h4,h5,h6,' +
      '.product-title,[data-product-title],[itemprop="name"],' +
      '.card__heading,.product-grid-item__title,.product__title,' +
      // Mosaic title patterns (expanded):
      '.mosaic__title,.mosaic__heading,.mosaic__text-title,.tile__heading,.tile__title,[data-tile-title],[data-mosaic-title]'
    );

    return !!norm(title?.textContent);
  }

  function linkHasName(a){
    if (!a) return false;
    if (norm(a.getAttribute('aria-label'))) return true;
    if (a.hasAttribute('aria-labelledby')){
      const ids = a.getAttribute('aria-labelledby').split(/\s+/).filter(Boolean);
      const txt = norm(ids.map(id => document.getElementById(id)?.textContent || '').join(' '));
      if (txt) return true;
    }
    if (norm(a.innerText || a.textContent)) return true;
    if (norm(a.getAttribute('title'))) return true;
    return false;
  }

  // Decorative <img> => alt="" (don't force aria-hidden on images)
  function makeDecorative(img){
    if (!img || !(img instanceof Element)) return;
    if (img.hasAttribute('data-a11y-keep-alt')) return;
    img.setAttribute('alt','');
    img.removeAttribute('aria-hidden');
    img.removeAttribute('aria-label');
    img.removeAttribute('aria-labelledby');
  }

  // Don't blank logos/press marks (unless you opt into press de-dupe)
  function isLogoLike(img){
    if (!img) return false;
    const alt = norm(img.getAttribute('alt')).toLowerCase();
    if (/\blogo\b/.test(alt)) return true;
    if (img.closest('.press, [data-press-items], .press__items, .press__logos-slider')) return true;
    return false;
  }

  function removeLabelsCopiedFromImgAlt(root=document){
    if (!FLAGS.REMOVE_LABELS_COPIED_FROM_IMG_ALT) return;

    $$('a, [role="link"]', root).forEach(a => {
      const aria = norm(a.getAttribute('aria-label'));
      if (!aria) return;

      const img = a.querySelector('img[alt]:not([alt=""])');
      if (!img) return;

      const alt = norm(img.getAttribute('alt'));
      if (!alt || alt !== aria) return;

      a.removeAttribute('aria-label');
      if (!hasA11yName(a)) a.setAttribute('aria-label', aria);
    });
  }

  function decorativeImagesInsideNamedLinks(root=document){
    $$('a', root).forEach(a => {
      if (!linkHasName(a)) return;
      a.querySelectorAll('img[alt]').forEach(img => {
        if (isLogoLike(img)) return;
        makeDecorative(img);
      });
    });
  }

  function gridAltDedup(root=document){
    if (!FLAGS.BLANK_GRID_ALTS) return;

    $$('img', root).forEach(img => {
      if (!img.closest(GRID_CONTAINERS)) return;
      if (img.hasAttribute('data-a11y-keep-alt')) return;

      // Keep logos unless user opts into press dedupe behavior
      if (isLogoLike(img) && !FLAGS.PRESS_LOGO_ALT_DEDUP) return;

      if (!FLAGS.BLANK_RELATED_ALTS && inRelated(img)) return;

      const c = card(img);
      if (!c) return;

      const hasTitle = cardHasIndependentName(c);
      const enclosingLink = img.closest('a');
      const linkNamed = enclosingLink && linkHasName(enclosingLink);

      if (hasTitle || (FLAGS.DECORATE_ONLY_IN_LINKS && linkNamed)) makeDecorative(img);
    });
  }

  // Optional: press/logo slider de-dupe (keeps first instance, blanks duplicates)
  function pressLogoAltDedup(root=document){
    if (!FLAGS.PRESS_LOGO_ALT_DEDUP) return;
    const containers = $$('.press__logos-slider, .press__items, [data-press-items], .press', root);
    containers.forEach(c => {
      const seen = new Set();
      $$('img[alt]:not([alt=""])', c).forEach(img => {
        const alt = norm(img.getAttribute('alt')).toLowerCase();
        if (!alt) return;
        if (seen.has(alt)) makeDecorative(img);
        else seen.add(alt);
      });
    });
  }

  // Filter semantics fixes (availability, price)
  function filterSemanticsFixes(root=document){
    $$('.collection__filter-tags-wrapper[data-collapsible-container]', root).forEach(panel => {
      if (panel.hasAttribute('data-a11y-filterpanel')) return;
      panel.setAttribute('data-a11y-filterpanel','true');

      if (!panel.id) panel.id = 'filter-panel-' + Math.random().toString(36).slice(2,8);

      let btn = panel.previousElementSibling;
      const initialExpanded = (panel.getAttribute('aria-expanded') || 'true') === 'true';

      if (!btn || btn.tagName !== 'BUTTON' || !btn.classList.contains('filter__toggle')) {
        btn = document.createElement('button');
        btn.className = 'filter__toggle';
        btn.type = 'button';
        btn.textContent = panel.getAttribute('data-filter-title') || panel.dataset.title || 'Filter';
        panel.parentNode.insertBefore(btn, panel);
      }

      btn.id ||= 'filter-btn-' + Math.random().toString(36).slice(2,8);
      btn.setAttribute('aria-controls', panel.id);
      btn.setAttribute('aria-expanded', String(initialExpanded));

      panel.setAttribute('role','region');
      panel.setAttribute('aria-labelledby', btn.id);
      panel.removeAttribute('aria-expanded');

      if (markOnce(btn, 'data-a11y-filterbtn')) {
        btn.addEventListener('click', () => {
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!isOpen));
          panel.hidden = isOpen;
          if (!isOpen) panel.style.height = 'auto';
        }, { passive: true });
      }
    });

    $$('[id*="CollectionFilter"][data-collapsible-container]', root).forEach(panel => {
      const min = panel.querySelector('input[name*="price"][name*="gte"], input[name*="price_min"], input#price-min') ||
                  panel.querySelector('input[type="number"]:first-of-type');
      const max = panel.querySelector('input[name*="price"][name*="lte"], input[name*="price_max"], input#price-max') ||
                  panel.querySelector('input[type="number"]:nth-of-type(2)');
      if (!min && !max) return;

      const currencySymbol = norm(panel.querySelector('.currency')?.textContent) || '$';

      function ensureLabeled(input, which){
        if (!input || input.hasAttribute('data-a11y-price')) return;
        input.setAttribute('data-a11y-price','true');

        input.type = 'number';
        input.inputMode = 'numeric';
        input.min = input.min || '0';
        if (!input.id) input.id = which === 'min' ? 'price-min' : 'price-max';

        if (!panel.querySelector(`label[for="${CSS.escape(input.id)}"]`)){
          const label = document.createElement('label');
          label.setAttribute('for', input.id);
          label.textContent = which === 'min' ? 'From' : 'To';
          input.parentNode.insertBefore(label, input);
        }

        let desc = document.getElementById(`currency-${which}`);
        if (!desc){
          desc = document.createElement('span');
          desc.id = `currency-${which}`;
          desc.className = 'currency';
          desc.setAttribute('aria-hidden','true');
          desc.textContent = currencySymbol;
          input.parentNode.insertBefore(desc, input);
        }

        const ids = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        ids.add(desc.id);
        input.setAttribute('aria-describedby', Array.from(ids).join(' '));
      }

      ensureLabeled(min, 'min');
      ensureLabeled(max, 'max');
    });
  }

  // ---------- Hidden-content / "semantic-hidden" hardening ----------
  function semanticHiddenHardening(root=document){
    if (!FLAGS.SEMANTIC_HIDDEN_HARDENING) return;

    const inertSupported = 'inert' in HTMLElement.prototype;

    // (A) Harden everything that is explicitly hidden (aria-hidden="true" or [hidden])
    const hiddenContainers = root?.matches?.('[aria-hidden="true"],[hidden]') ? [root] : $$('[aria-hidden="true"],[hidden]', root);

    hiddenContainers.forEach(container => {
      if (!(container instanceof Element)) return;

      // Remove tabindex from the container itself (audit often flags tabindex on aria-hidden nodes)
      if (container.hasAttribute('tabindex')) {
        if (!container.hasAttribute('data-a11y-prev-tabindex')) {
          container.setAttribute('data-a11y-prev-tabindex', container.getAttribute('tabindex'));
        }
        container.removeAttribute('tabindex');
      }

      // Apply inert if supported (best fix)
      if (inertSupported && !container.hasAttribute('inert')) {
        container.setAttribute('inert','');
        container.setAttribute('data-a11y-set-inert','true');
      }

      // Fallback: make all focusables untabbable, store previous tabindex (if any)
      const focusables = $$(focusableSel, container);
      focusables.forEach(el => {
        if (!(el instanceof Element)) return;

        if (!el.hasAttribute('data-a11y-prev-tabindex')) {
          const prev = el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '__none__';
          el.setAttribute('data-a11y-prev-tabindex', prev);
        }
        el.setAttribute('tabindex','-1');
      });
    });

    // (B) Remove aria-hidden that is being applied to visible, meaningful content (conservative)
    const maybeBad = root?.matches?.('[aria-hidden="true"]') ? [root] : $$('[aria-hidden="true"]', root);
    maybeBad.forEach(el => {
      if (!(el instanceof Element)) return;
      if (!isVisible(el)) return;

      const tag = (el.tagName || '').toUpperCase();
      if (['SVG','PATH','IMG','SPAN','I','USE'].includes(tag)) return;
      if ((el.className || '').toString().match(/\b(icon|sr-only|visually-hidden)\b/i)) return;

      const hasContent = norm(el.innerText).length > 0 || el.querySelector('a[href],button,input,select,textarea');
      if (!hasContent) return;

      if (el.hasAttribute('data-a11y-keep-ariahidden')) return;

      el.removeAttribute('aria-hidden');
      el.setAttribute('data-a11y-ariahidden-removed','true');

      // If we inerted it because of aria-hidden, remove inert too (now that it's visible)
      if (el.hasAttribute('data-a11y-set-inert')) {
        el.removeAttribute('inert');
        el.removeAttribute('data-a11y-set-inert');
      }
    });

    // (C) Restore tabindex when elements are no longer hidden
    const restored = root?.matches?.('[data-a11y-prev-tabindex]') ? [root] : $$('[data-a11y-prev-tabindex]', root);
    restored.forEach(el => {
      if (!(el instanceof Element)) return;
      if (isHidden(el)) return; // still hidden

      const prev = el.getAttribute('data-a11y-prev-tabindex');
      el.removeAttribute('data-a11y-prev-tabindex');

      if (prev === '__none__' || prev === null) el.removeAttribute('tabindex');
      else el.setAttribute('tabindex', prev);
    });

    // (D) IMPORTANT: Remove inert we added when element becomes visible again (Flickity slides)
    const managedInert = root?.matches?.('[data-a11y-set-inert]') ? [root] : $$('[data-a11y-set-inert]', root);
    managedInert.forEach(el => {
      if (!(el instanceof Element)) return;

      // If it is no longer actually hidden, remove inert and our marker
      if (!isHidden(el)) {
        el.removeAttribute('inert');
        el.removeAttribute('data-a11y-set-inert');
      }
    });
  }
  window.__semanticHiddenHardening = semanticHiddenHardening;

  function postPassRun(root=document){
    removeLabelsCopiedFromImgAlt(root);
    decorativeImagesInsideNamedLinks(root);
    gridAltDedup(root);
    pressLogoAltDedup(root);
    filterSemanticsFixes(root);
    semanticHiddenHardening(root);
  }

  // ---------- single scheduler + single observer ----------
  let scheduled = false;
  let pendingRoot = document;

  function scheduleRun(root=document){
    pendingRoot = root || document;
    if (scheduled) return;
    scheduled = true;

    setTimeout(() => {
      scheduled = false;

      const r = pendingRoot || document;

      enhanceExternalLinks(r);
      labelIconControls(r);
      addScopedLabels(r);
      armExistingDialogs(r);
      pauseAutoplay();
      fixFlickityTabindex(r);

      ensureUniqueNavNames();
      fixAnnouncementLandmarks(document);
      fixEmptyLinks(r);
      fixIframes(r);
      upsellInert(r);
      containWhatmore(r);
      normalizeBanners(document);
      labelBundleSelects(r);
      nameATCButtons(r);
      buttonizeNonButtons(r);
      watchCart();

      postPassRun(r);
    }, 90);
  }

  function shouldScheduleForMutation(m){
    if (m.type === 'childList' && m.addedNodes?.length) return true;
    if (m.type === 'attributes') {
      const t = m.target;
      if (!(t instanceof Element)) return false;

      if (t.matches('img')) return true;
      if (t.matches('[data-upsell-holder]')) return true;
      if (t.matches('.flickity-enabled,[data-flickity],.flickity-viewport,.flickity-slider')) return true;
      if (t.matches('[data-announcement-wrapper], .shopify-section.announcement-bar-static')) return true;
      if (t.matches('a,button,[role="button"],[role="dialog"],.modal,.drawer,dialog')) return true;

      // React to hidden-state changes for semantic-hidden hardening
      if (t.matches('[aria-hidden],[hidden],[tabindex], [inert]')) return true;
    }
    return false;
  }

  ready(() => {
    scheduleRun(document);

    document.addEventListener('shopify:section:load', e => scheduleRun(e.target));
    document.addEventListener('shopify:section:unload', e => scheduleRun(e.target));
    document.addEventListener('theme:section:load', e => scheduleRun(e.target));
    window.addEventListener('load', () => scheduleRun(document));

    const observer = new MutationObserver(muts => {
      for (const m of muts){
        if (shouldScheduleForMutation(m)) { scheduleRun(document); return; }
      }
    });

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'alt','src','srcset',
          'aria-hidden','tabindex','open','hidden',
          'aria-label','aria-labelledby','role',
          'class','style','inert'
        ]
      });
    }

    setTimeout(() => scheduleRun(document), 1400);
  });

})();